import { SelectProps } from "@/components/Inputs/Select/SelectProps.ts";
import { Loader } from "@/components/Loader/Loader.tsx";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AiOutlineClose } from "react-icons/ai";

/**
 * Generic component that mimics HTML <select> element because of the need for custom styles
 */
export const Select = (props: SelectProps) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState<string>("");
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleClickOutside = (event: MouseEvent) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
      setIsOpen(false);
    }
  };

  const clearSelection = () => {
    setSelectedOption("");
    props.onSelect("");
  };

  const handleSetSelectedOption = useCallback(() => {
    const viewedOption = props.options.find((opt) =>
      props.optionValueKey ? opt[props.optionValueKey] === props.value : opt.id === props.value,
    );
    const value = viewedOption?.name || "";
    if (viewedOption) setSelectedOption(value);
  }, [props.options, props.optionValueKey, props.value]);

  // set initial value
  useEffect(() => {
    if (props.value) handleSetSelectedOption();
    else setSelectedOption(t("selectAccType"));
  }, [t, setSelectedOption, props.value, handleSetSelectedOption]);

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const isPlaceHolderValue = selectedOption === t("selectAccType");

  const handleSelect = (option: string) => {
    props.onSelect(option);
    setIsOpen(false);
  };

  return (
    <div
      className={`selectWrap ${isOpen ? "dropdownOpen" : ""}`}
      ref={dropdownRef}
      onClick={() => setIsOpen(!isOpen)}
    >
      <span className="label">{t("accommodationType")}</span>
      <div className={`${isPlaceHolderValue ? "selectComponent placeholder" : "selectComponent"}`}>
        {props?.loading ? <Loader loading component={<div>{t("loading")}...</div>} /> : selectedOption}
      </div>
      {isOpen && (
        <div className="selectDropdownMenu">
          {props.options?.map((option, index) => (
            <div
              key={index}
              onClick={() => handleSelect(props.optionValueKey ? option[props.optionValueKey] : option?.id)}
            >
              {option?.name}
            </div>
          ))}
        </div>
      )}
      {isOpen && selectedOption && (
        <div className="clearSelection">
          <AiOutlineClose onClick={clearSelection} />
        </div>
      )}
    </div>
  );
};
